import ProblemDetailsError from '../api/ProblemDetailsError';
import isProblemDetails from './isProblemDetails';
import isPrimitiveType from './isPrimitiveType';
import isAuthError from './isAuthError';
import {ProblemDetails} from '../types';
import hasMessageProperty from './hasMessageProperty';

const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const hasToString = (value: unknown): value is {toString(): string} =>
    isObject(value) && typeof value.toString === 'function';

function unknownErrorToString(err: unknown): string {
    if (err === null || err === undefined) {
        return '';
    }

    if (typeof err === 'string') {
        return err;
    }

    if (err instanceof ProblemDetailsError) {
        return problemDetailsToString(err.problemDetails);
    }

    if (err instanceof Error) {
        return err.message;
    }

    if (isProblemDetails(err)) {
        return problemDetailsToString(err);
    }

    if (isAuthError(err)) {
        return `${err.error}: ${err.error_description}`;
    }

    if (hasMessageProperty(err)) {
        return err.message ?? 'An error occurred.';
    }

    if (isPrimitiveType(err)) {
        return String(err);
    }

    if (hasToString(err)) {
        return err.toString();
    }

    try {
        const remainingObject = err as object;
        return JSON.stringify(remainingObject);
    } catch {
        console.error(err);
        return 'An unexpected error occurred.';
    }
}

function problemDetailsToString(problemDetails: ProblemDetails): string {
    const messages: string[] = [];

    if (problemDetails.title) {
        messages.push(problemDetails.title);
    }

    if (problemDetails.detail) {
        messages.push(problemDetails.detail);
    }

    if (problemDetails.errors && problemDetails.errors.length > 0) {
        const errorMessages = problemDetails.errors
            .map(error => error.message)
            .filter((message): message is string => message != null);
        messages.push(...errorMessages);
    }

    return messages.join(' ') || 'An unexpected error occurred.';
}

export default unknownErrorToString;
